import {
  CalendarIcon,
  ChevronDownIcon,
  LinkIcon,
  ListChecksIcon,
  PaperclipIcon,
  ToggleLeftIcon,
  TypeIcon,
  type LucideIcon,
} from 'lucide-react';

export type BriefSectionType =
  | 'text'
  | 'date'
  | 'select'
  | 'checklist'
  | 'url'
  | 'file'
  | 'boolean';

interface BriefSectionTypeMetaShape {
  type: BriefSectionType;
  /** i18n key reference only — resolve at render time with `t()`, never at module scope. */
  labelKey: string;
  /** i18n key reference only — resolve at render time with `t()`, never at module scope. */
  hintKey: string;
  Icon: LucideIcon;
}

/**
 * Exactly seven types, in the order the type picker presents them. `as const satisfies`
 * (not a type annotation) keeps each `labelKey`/`hintKey` as its own string-literal type,
 * so `t(entry.labelKey)` type-checks against the compile-time-checked key union — a plain
 * `: BriefSectionTypeMeta[]` annotation would widen every key to `string` and fail `tsc`.
 *
 * `no-unsafe-assignment` misfires on every `Icon:` value below: lucide-react's exported icon
 * components resolve as an error type under this project's current typescript-eslint type
 * resolution, reproducible even on pre-existing, unmodified files using the identical
 * `Icon: LucideIcon` pattern (e.g. `src/features/knowledge-bases/lib/file-type-cell.ts`), so
 * this is a tooling/environment gap rather than a real type-safety issue — each value here is
 * a valid `LucideIcon` component.
 */

export const BRIEF_SECTION_TYPES = [
  {
    type: 'text',
    labelKey: 'knowledgeHub.briefSectionTypePicker.text.label',
    hintKey: 'knowledgeHub.briefSectionTypePicker.text.hint',
    Icon: TypeIcon,
  },
  {
    type: 'date',
    labelKey: 'knowledgeHub.briefSectionTypePicker.date.label',
    hintKey: 'knowledgeHub.briefSectionTypePicker.date.hint',
    Icon: CalendarIcon,
  },
  {
    type: 'select',
    labelKey: 'knowledgeHub.briefSectionTypePicker.select.label',
    hintKey: 'knowledgeHub.briefSectionTypePicker.select.hint',
    Icon: ChevronDownIcon,
  },
  {
    type: 'checklist',
    labelKey: 'knowledgeHub.briefSectionTypePicker.checklist.label',
    hintKey: 'knowledgeHub.briefSectionTypePicker.checklist.hint',
    Icon: ListChecksIcon,
  },
  {
    type: 'url',
    labelKey: 'knowledgeHub.briefSectionTypePicker.url.label',
    hintKey: 'knowledgeHub.briefSectionTypePicker.url.hint',
    Icon: LinkIcon,
  },
  {
    type: 'file',
    labelKey: 'knowledgeHub.briefSectionTypePicker.file.label',
    hintKey: 'knowledgeHub.briefSectionTypePicker.file.hint',
    Icon: PaperclipIcon,
  },
  {
    type: 'boolean',
    labelKey: 'knowledgeHub.briefSectionTypePicker.boolean.label',
    hintKey: 'knowledgeHub.briefSectionTypePicker.boolean.hint',
    Icon: ToggleLeftIcon,
  },
] as const satisfies readonly BriefSectionTypeMetaShape[];

export type BriefSectionTypeMeta = (typeof BRIEF_SECTION_TYPES)[number];

export const SECTION_TYPE_ICON: Record<BriefSectionType, LucideIcon> = Object.fromEntries(
  BRIEF_SECTION_TYPES.map((entry) => [entry.type, entry.Icon]),
) as Record<BriefSectionType, LucideIcon>;

const BRIEF_SECTION_TYPE_SET = new Set<string>(BRIEF_SECTION_TYPES.map((entry) => entry.type));

/**
 * Validates an external value (an API response field) against the seven known types,
 * falling back to `'text'` for anything absent or unrecognized — including a value from a
 * deployed backend that predates this migration, which omits the field entirely rather than
 * sending `null`. Parsing external input at the boundary, per `AGENTS.md`'s Validation rule;
 * a bare `as BriefSectionType` cast let `undefined` reach `SECTION_TYPE_ICON` and crash.
 */
export function resolveBriefSectionType(value: unknown): BriefSectionType {
  return typeof value === 'string' && BRIEF_SECTION_TYPE_SET.has(value)
    ? (value as BriefSectionType)
    : 'text';
}

/**
 * i18n key reference only — resolve at render time with `t()`, never at module scope. Used by
 * the section row to title its type icon (e.g. "Text", "Check list") the way the prototype does.
 */
export const SECTION_TYPE_LABEL_KEY = Object.fromEntries(
  BRIEF_SECTION_TYPES.map((entry) => [entry.type, entry.labelKey]),
  // The value type is the union of the seven literal keys, read back off the table rather
  // than written as `string`: widening here is the same mistake the table itself avoids with
  // `as const satisfies`, and it puts `t()` back on its untyped overload.
) as Record<BriefSectionType, (typeof BRIEF_SECTION_TYPES)[number]['labelKey']>;

/**
 * Mirrors the server's `DEFAULT_SECTION_CONFIG`
 * (`apps/assets-api/src/brief-templates/default-section-config.ts`), pinned equal by
 * `sectionTypes.test.ts`, which reads that file rather than a copy of its values, so a change
 * on either side turns it red. English only: the server has no i18n of its own, so English is
 * the one locale the two sides must agree on byte for byte. A new section seeds its config
 * from this table.
 *
 * `trueLabel`/`falseLabel` stay a plain literal, not a `t()` call, for the same reason as
 * `format`/`optionSource` below: this is a **seed value for author-editable persisted data**,
 * not fixed UI chrome. The drawer's `BooleanConfigFields` lets the author rewrite both labels
 * per section, and whatever they land on is what a briefer eventually reads — this constant
 * only supplies the untouched starting point, and it must equal the backend's canonical English
 * default byte-for-byte (see the pinned test above). Translating it here would desync the two
 * sides for every non-English locale and contradicts the byte-for-byte contract; localizing the
 * *author's own* label text, if ever desired, belongs in the drawer as an editable default, not
 * in this shared contract constant. `BriefTemplatePreviewSection.tsx` applies the identical
 * reasoning to its own fallback.
 */
export const DEFAULT_SECTION_CONFIG: Record<BriefSectionType, Record<string, unknown>> = {
  text: { maxLength: null },
  // eslint-disable-next-line i18next/no-literal-string -- protocol value: DateSectionConfigDto's `@IsIn(['Start date only', 'Start + End date'])` validates this exact string server-side; translating it would break the wire contract.
  date: { format: 'Start date only' },
  select: {
    // eslint-disable-next-line i18next/no-literal-string -- protocol value, not copy: `optionSource` is an open enum the server stores and round-trips verbatim (`@IsString() @MaxLength(64)`, no `@IsIn`), so the English label IS the stored value across every locale.
    optionSource: 'Manual list',
    options: [''],
    multiple: false,
  },
  checklist: {
    // eslint-disable-next-line i18next/no-literal-string -- see the `select` case above — same shared persisted shape.
    optionSource: 'Manual list',
    options: [''],
    multiple: true,
  },
  url: { allowedSchemes: ['https'] },
  file: { acceptedMimeTypes: ['application/pdf'], maxSizeBytes: 10485760, maxCount: 1 },
  boolean: {
    // eslint-disable-next-line i18next/no-literal-string -- seed default for an author-editable persisted label, pinned byte-for-byte equal to the backend's English default; see the block comment above.
    trueLabel: 'Yes',
    // eslint-disable-next-line i18next/no-literal-string -- see `trueLabel` above.
    falseLabel: 'No',
  },
};
